/**
 * MobilePlanningList — mobile view of the capacity planner.
 * Compact single-line rows grouped by week (like desktop) — tap a row to
 * open a bottom-sheet drawer with the full task card for edits + actions.
 */
import React from "react";
import {
  AlertTriangle, ClockAlert, Edit3, Trash2, ChevronDown, ChevronRight, Plus,
  Users, Calendar, X,
} from "lucide-react";
import { STATUS_META, statusOptions, fmtDateShort, calcBudgetNok, fmtNok, effectiveHours } from "./constants";
import { StatusPill } from "./atoms";

/** Convert `2026-W29` → "13 Jul – 19 Jul" for the week-group header. */
function weekRangeLabel(isoWeek) {
  if (!isoWeek || isoWeek === "unscheduled") return "";
  const m = /^(\d{4})-W(\d{2})$/.exec(isoWeek);
  if (!m) return "";
  const year = parseInt(m[1], 10);
  const week = parseInt(m[2], 10);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Day = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Day + 1 + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const opts = { day: "2-digit", month: "short" };
  return `${monday.toLocaleDateString("en-GB", opts)} – ${sunday.toLocaleDateString("en-GB", opts)}`;
}

export default function MobilePlanningList({
  rows,
  drafts = {},
  onEditProject,
  onCarryOver,
  onDeleteSchedule,
  onSaveStatus,
  onDraftChange,
  onDraftSave,
  onDraftDiscard,
  onInsertRowForWeek,
  currentWeek,
  currentWeekFrameColor = "#10b981",
  statusColors = {},
  emptyMsg,
}) {
  const grouped = React.useMemo(() => {
    const byWeek = new Map();
    for (const r of rows) {
      const k = r.week || "unscheduled";
      if (!byWeek.has(k)) byWeek.set(k, []);
      byWeek.get(k).push(r);
    }
    for (const arr of byWeek.values()) {
      arr.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    }
    return Array.from(byWeek.entries()).map(([week, rs]) => ({
      week, rows: rs, drafts: drafts[week] || [],
    }));
  }, [rows, drafts]);

  const [collapsed, setCollapsed] = React.useState(new Set());
  const toggle = (week) => setCollapsed((cur) => {
    const nx = new Set(cur);
    if (nx.has(week)) nx.delete(week); else nx.add(week);
    return nx;
  });

  const [detailRow, setDetailRow] = React.useState(null);
  const [detailDraft, setDetailDraft] = React.useState(null);

  if (!rows.length && !Object.values(drafts).flat().length) {
    return (
      <div className="rounded-xl border border-white/10 bg-[#0f1a2e] p-8 text-center text-slate-500 italic">
        {emptyMsg || "No projects scheduled yet."}
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3" data-testid="mobile-planning-list">
        {grouped.map(({ week, rows: rs, drafts: ds }) => {
          const isCurrent = week === currentWeek || (rs[0] && rs[0].is_current);
          const isCollapsed = collapsed.has(week);
          const totalBudget = rs.reduce((sum, r) => sum + calcBudgetNok(r.planned_workers, r.working_days, r.total_hours), 0);
          return (
            <section key={week}
                     className="rounded-xl border overflow-hidden bg-[#0f1a2e]"
                     style={{
                       borderColor: isCurrent ? currentWeekFrameColor : "rgba(255,255,255,0.08)",
                       borderWidth: isCurrent ? "3px" : "1px",
                     }}>
              <button type="button"
                      onClick={() => toggle(week)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 bg-slate-800/60 hover:bg-slate-800/80 border-b border-white/5"
                      data-testid={`m-week-header-${week}`}>
                {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400"/> : <ChevronDown className="w-4 h-4 text-slate-400"/>}
                <div className="flex-1 text-left">
                  <div className="text-sm font-black text-slate-100 font-mono">
                    {week}
                    {isCurrent && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded"
                            style={{ background: currentWeekFrameColor + "33", color: currentWeekFrameColor }}>
                        Now
                      </span>
                    )}
                  </div>
                  {weekRangeLabel(week) && (
                    <div className="text-[11px] text-slate-300 font-semibold mt-0.5">
                      {weekRangeLabel(week)}
                    </div>
                  )}
                  <div className="text-[10px] text-slate-400">
                    {rs.length + ds.length} {(rs.length + ds.length) === 1 ? "task" : "tasks"} · {fmtNok(totalBudget)} NOK
                  </div>
                </div>
                {onInsertRowForWeek && (
                  <button type="button"
                          onClick={(e) => { e.stopPropagation(); onInsertRowForWeek(week); }}
                          className="p-1.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900"
                          data-testid={`m-add-row-${week}`}
                          title="Add task to this week">
                    <Plus className="w-3.5 h-3.5"/>
                  </button>
                )}
              </button>

              {!isCollapsed && (
                <ul className="divide-y divide-white/5">
                  {ds.map((d) => (
                    <li key={d.id}>
                      <button type="button"
                              onClick={() => setDetailDraft(d)}
                              className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-cyan-500/5 active:bg-cyan-500/10 text-left"
                              data-testid={`m-draft-row-${d.id}`}>
                        <span className="text-[9px] uppercase tracking-widest font-black px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-200 flex-shrink-0">Draft</span>
                        <span className="flex-1 text-sm text-slate-200 font-semibold truncate">
                          {d.task_name || <span className="italic text-slate-500">Untitled draft…</span>}
                        </span>
                        <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0"/>
                      </button>
                    </li>
                  ))}
                  {rs.map((row) => <TaskLineRow key={row.schedule_id} row={row} onOpen={setDetailRow} statusColors={statusColors}/>)}
                </ul>
              )}
            </section>
          );
        })}
      </div>

      {detailRow && (
        <DetailDrawer onClose={() => setDetailRow(null)} title={detailRow.project_name}>
          <TaskCard row={detailRow}
                    onEditProject={(pid) => { onEditProject(pid); setDetailRow(null); }}
                    onCarryOver={(pid) => { onCarryOver(pid); setDetailRow(null); }}
                    onDeleteSchedule={(sid) => { onDeleteSchedule(sid); setDetailRow(null); }}
                    onSaveStatus={onSaveStatus}
                    statusColors={statusColors}/>
        </DetailDrawer>
      )}

      {detailDraft && (
        <DetailDrawer onClose={() => setDetailDraft(null)} title={detailDraft.task_name || "New draft"}>
          <DraftCard draft={detailDraft}
                     onChange={onDraftChange}
                     onSave={(d) => { onDraftSave(d); setDetailDraft(null); }}
                     onDiscard={(id) => { onDraftDiscard(id); setDetailDraft(null); }}/>
        </DetailDrawer>
      )}
    </>
  );
}

/** Compact row visible in the list — mirrors the desktop table cadence. */
function TaskLineRow({ row, onOpen, statusColors }) {
  const overdue = !!row.is_overdue;
  const dot = statusColors?.[row.status] || STATUS_META[row.status]?.dot || "#94a3b8";
  return (
    <li>
      <button type="button"
              onClick={() => onOpen(row)}
              className={`w-full flex items-center gap-2 px-3 py-2.5 text-left active:bg-slate-800/40 ${row.is_shadow ? "opacity-50" : "hover:bg-white/[0.03]"}`}
              data-testid={`m-task-row-${row.schedule_id}`}>
        <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: dot }}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <span className="text-sm font-black text-slate-100 truncate">{row.project_name}</span>
            {overdue && (
              <span className="text-[9px] uppercase tracking-widest font-black text-red-200 bg-red-500/20 rounded px-1 flex-shrink-0 inline-flex items-center gap-0.5">
                <AlertTriangle className="w-2.5 h-2.5"/>{row.days_overdue}d
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[10px] text-slate-400 font-mono mt-0.5">
            <span>{fmtDateShort(row.expected_start)}→{fmtDateShort(row.expected_finish)}</span>
            <span className="inline-flex items-center gap-0.5"><Users className="w-2.5 h-2.5"/>{row.planned_workers}</span>
            <span>{typeof row.working_days === "number" ? row.working_days.toFixed(1) : row.working_days || "—"}d</span>
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0"/>
      </button>
    </li>
  );
}

/** Bottom-sheet drawer used to host details of the tapped row/draft. */
function DetailDrawer({ onClose, title, children }) {
  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center" data-testid="m-detail-drawer">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose}/>
      <div className="relative w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl bg-[#0f1a2e] border-t border-white/10 sm:border shadow-2xl max-h-[92vh] overflow-hidden flex flex-col animate-[slideUp_.2s_ease-out]"
           style={{ animation: "slideUp .22s cubic-bezier(.2,.9,.3,1)" }}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-slate-900/40">
          <span className="w-10 h-1 rounded-full bg-white/20 mx-auto sm:hidden absolute left-1/2 top-1.5 -translate-x-1/2"/>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Detalii</div>
            <div className="text-sm font-black text-slate-100 truncate">{title}</div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-slate-400" data-testid="m-detail-close" aria-label="Close">
            <X className="w-5 h-5"/>
          </button>
        </div>
        <div className="p-3 overflow-y-auto">{children}</div>
      </div>
      <style>{`@keyframes slideUp { from { transform: translateY(30px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
    </div>
  );
}

/** Editable draft form (previously inline — now used inside the drawer). */
function DraftCard({ draft, onChange, onSave, onDiscard }) {
  const d = draft;
  return (
    <div className="space-y-3">
      <div>
        <div className="text-[10px] uppercase tracking-widest font-black text-slate-400 mb-1">Task</div>
        <input value={d.task_name} placeholder="Task name…"
               onChange={(e) => onChange(d.id, { task_name: e.target.value })}
               className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm font-semibold text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"/>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-slate-400 mb-1">Members</div>
          <input type="number" value={d.planned_workers}
                 onChange={(e) => onChange(d.id, { planned_workers: e.target.value })}
                 className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"/>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-slate-400 mb-1">Days</div>
          <input type="number" step="0.5" value={d.working_days}
                 onChange={(e) => onChange(d.id, { working_days: e.target.value })}
                 className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400"/>
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest font-black text-slate-400 mb-1">Comments</div>
        <textarea value={d.comments} placeholder="Comments…"
                  onChange={(e) => onChange(d.id, { comments: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-900/60 border border-white/10 rounded-lg px-3 py-2 text-sm text-slate-100 focus:outline-none focus:ring-1 focus:ring-cyan-400 resize-none"/>
      </div>
      <div className="flex gap-2 pt-2">
        <button onClick={() => onSave(d)}
                className="flex-1 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-black text-xs uppercase tracking-widest"
                data-testid={`m-draft-save-${d.id}`}>Save</button>
        <button onClick={() => onDiscard(d.id)}
                className="px-4 py-3 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 text-xs uppercase tracking-widest">Discard</button>
      </div>
    </div>
  );
}

function TaskCard({ row, onEditProject, onCarryOver, onDeleteSchedule, onSaveStatus, statusColors }) {
  const overdue = !!row.is_overdue;
  const hours = effectiveHours(row);
  const budget = calcBudgetNok(row.planned_workers, row.working_days, row.total_hours);
  const [showStatus, setShowStatus] = React.useState(false);

  return (
    <div className={`rounded-lg space-y-3 ${row.is_shadow ? "opacity-50" : ""}`}
         data-testid={`m-task-card-${row.schedule_id}`}>
      {row.address && (
        <div className="text-xs text-slate-400 -mt-1">{row.address}</div>
      )}

      {(overdue || row.carryover_from || row.is_shadow) && (
        <div className="flex flex-wrap gap-1.5">
          {overdue && (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-500/20 text-red-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
              <AlertTriangle className="w-3 h-3"/> Overdue {row.days_overdue}d
            </span>
          )}
          {row.carryover_from && (
            <span className="inline-flex items-center gap-1 rounded-full bg-purple-500/20 text-purple-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
              ↻ From {row.carryover_from.split("-W")[1]}
            </span>
          )}
          {row.is_shadow && (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/20 text-sky-200 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">
              ⇔ Spans multiple weeks
            </span>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-slate-900/40 border border-white/5 p-2.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1 inline-flex items-center gap-1"><Calendar className="w-3 h-3"/> Dates</div>
          <div className="text-sm font-mono text-slate-100">
            {fmtDateShort(row.expected_start)}
            <span className="text-slate-600 px-1">→</span>
            {fmtDateShort(row.expected_finish)}
          </div>
        </div>
        <div className="rounded-lg bg-slate-900/40 border border-white/5 p-2.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1 inline-flex items-center gap-1"><Users className="w-3 h-3"/> Effort</div>
          <div className="text-sm font-mono text-slate-100">
            {row.planned_workers} · {typeof row.working_days === "number" ? row.working_days.toFixed(1) : row.working_days || "—"}d · {hours.toFixed(1)}h
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 rounded-lg bg-slate-900/40 border border-white/5 px-3 py-2.5">
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-0.5">Status</div>
          {showStatus ? (
            <select autoFocus
                    defaultValue={row.status}
                    onChange={(e) => { onSaveStatus(row.project_id, e.target.value); setShowStatus(false); }}
                    onBlur={() => setShowStatus(false)}
                    className="px-2 py-1 rounded bg-cyan-500/10 border border-cyan-400/50 text-slate-100 text-xs focus:outline-none">
              {statusOptions(row.status).map((s) => (
                <option key={s} value={s} className="bg-slate-900">{STATUS_META[s].label}</option>
              ))}
            </select>
          ) : (
            <button type="button" onClick={() => !row.is_shadow && setShowStatus(true)}
                    className="focus:outline-none"
                    data-testid={`m-status-${row.schedule_id}`}>
              <StatusPill status={row.status}/>
            </button>
          )}
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-0.5">Budget</div>
          <div className="text-sm font-black text-emerald-300 font-mono">
            {fmtNok(budget)} <span className="text-[10px] text-emerald-500/70">NOK</span>
          </div>
        </div>
      </div>

      {row.comments && (
        <div className="rounded-lg bg-slate-900/40 border border-white/5 p-2.5">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black mb-1">Comments</div>
          <div className="text-sm text-slate-200 whitespace-pre-wrap">{row.comments}</div>
        </div>
      )}

      {!row.is_shadow && (
        <div className="grid grid-cols-3 gap-2 pt-2">
          <button onClick={() => onEditProject(row.project_id)}
                  className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-black uppercase tracking-widest"
                  data-testid={`m-edit-${row.project_id}`}>
            <Edit3 className="w-3.5 h-3.5"/> Edit
          </button>
          {overdue ? (
            <button onClick={() => onCarryOver(row.project_id)}
                    className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 text-xs font-black uppercase tracking-widest"
                    data-testid={`m-carryover-${row.schedule_id}`}>
              <ClockAlert className="w-3.5 h-3.5"/> Carry
            </button>
          ) : <div/>}
          <button onClick={() => onDeleteSchedule(row.schedule_id)}
                  className="inline-flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-200 text-xs font-black uppercase tracking-widest"
                  data-testid={`m-del-${row.schedule_id}`}>
            <Trash2 className="w-3.5 h-3.5"/> Delete
          </button>
        </div>
      )}
    </div>
  );
}

export { ChevronDown };
